import { clsx } from 'clsx';
import { NeobrutalButton } from '@/components/marketing/v2/NeobrutalButton';
import { usePricingMode } from './PricingContext';

type Plan = {
  num: string;
  name: string;
  tagline: string;
  amountMonthly: string;
  amountYearly: string;
  unit: string;
  savings: string;
  ctaLabel: string;
  ctaVariant: 'primary' | 'secondary' | 'dark';
  ctaTo: string;
  tag: string;
  featured?: boolean;
  customPrice?: boolean;
  featsBold: ReadonlyArray<string>;
  feats: ReadonlyArray<string>;
  featsMuted?: ReadonlyArray<string>;
};

const PLANS: ReadonlyArray<Plan> = [
  {
    num: '01',
    name: 'Gratuit',
    tagline: 'Démarrer',
    amountMonthly: '0',
    amountYearly: '0',
    unit: 'F CFA · pour toujours',
    savings: '',
    ctaLabel: 'Commencer',
    ctaVariant: 'secondary',
    ctaTo: '/register',
    tag: 'Tester Velko',
    featsBold: ['1 boutique', '5 produits', '1 employé'],
    feats: ['1 caisse desktop + 1 mobile', 'Mode offline illimité', 'Inventaire de base'],
    featsMuted: ['Clients avancés', 'Rapports détaillés', 'Mobile Money', 'IA'],
  },
  {
    num: '02',
    name: 'Starter',
    tagline: 'Petit commerce',
    amountMonthly: '5K',
    amountYearly: '50K',
    unit: 'F CFA / mois',
    savings: '= 167 F par jour · pour sortir du cahier',
    ctaLabel: 'Essayer 14 jours',
    ctaVariant: 'primary',
    ctaTo: '/register?plan=starter',
    tag: "Plan d'appel",
    featsBold: ['1 boutique', '50 produits'],
    feats: ['2 employés', '1 caisse desktop + 1 mobile', 'Clients + rapports basiques'],
    featsMuted: ['Multi-boutiques', 'Mobile Money', 'Retours et promotions', 'IA'],
  },
  {
    num: '03',
    name: 'Croissance',
    tagline: 'Sérieux',
    amountMonthly: '10K',
    amountYearly: '100K',
    unit: 'F CFA / mois',
    savings: "= 333 F par jour · l'offre recommandée",
    ctaLabel: 'Essayer 14 jours',
    ctaVariant: 'primary',
    ctaTo: '/register?plan=croissance',
    tag: 'Recommandé · commerçants sérieux',
    featured: true,
    featsBold: ['2 boutiques', '1 000 produits'],
    feats: [
      '5 employés',
      '3 caisses desktop + 3 mobiles',
      'Mobile Money + retours',
      'Rapports de vente + exports',
      '30 crédits IA / mois',
    ],
    featsMuted: ['Prévisions IA', 'API + intégrations'],
  },
  {
    num: '04',
    name: 'Pro',
    tagline: 'Commerce établi',
    amountMonthly: '20K',
    amountYearly: '200K',
    unit: 'F CFA / mois',
    savings: 'Tu récupères le coût dès 2 ventes par jour',
    ctaLabel: 'Essayer 14 jours',
    ctaVariant: 'primary',
    ctaTo: '/register?plan=pro',
    tag: 'Commerce établi',
    featsBold: ['5 boutiques', 'Produits illimités'],
    feats: [
      '15 employés',
      '5 desktop + 5 mobiles',
      'Rapports avancés',
      'Historique 36 mois',
      '100 crédits IA + prévisions',
      'Support prioritaire FR · 24h',
    ],
    featsMuted: ['API + account manager dédié'],
  },
  {
    num: '05',
    name: 'Business',
    tagline: 'Réseaux',
    amountMonthly: 'Sur devis',
    amountYearly: 'Sur devis',
    unit: 'sur mesure',
    savings: 'Franchises · chaînes · réseaux nationaux',
    ctaLabel: "Parler à l'équipe",
    ctaVariant: 'dark',
    ctaTo: '/contact',
    tag: 'Accompagnement dédié',
    customPrice: true,
    featsBold: ['Boutiques illimitées', 'Appareils & employés illimités'],
    feats: [
      'Historique illimité',
      'SLA 99,9% + account manager',
      'API + intégrations sur mesure',
      'Formation sur site incluse',
      '200+ crédits IA',
    ],
  },
];

function CheckIcon({ bold = false }: { bold?: boolean }) {
  return (
    <span
      className={clsx(
        'mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center border-2 border-charcoal-800',
        bold ? 'bg-lime-500 text-charcoal-800' : 'bg-white text-lime-600',
      )}
    >
      <svg
        width="11"
        height="11"
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.8"
      >
        <path d="M2.5 6l2.5 2.5 5-5" />
      </svg>
    </span>
  );
}

function MutedIcon() {
  return (
    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center border-2 border-charcoal-300 bg-charcoal-50 text-charcoal-300">
      <svg
        width="10"
        height="10"
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
      >
        <path d="M3 3l6 6" />
        <path d="M9 3L3 9" />
      </svg>
    </span>
  );
}

function PlanCard({ p, mode, inCarousel = false }: { p: Plan; mode: 'monthly' | 'yearly'; inCarousel?: boolean }) {
  const amount = mode === 'yearly' ? p.amountYearly : p.amountMonthly;
  const unit = p.customPrice ? p.unit : mode === 'yearly' ? 'F CFA / an' : p.unit;

  return (
    <article
      className={clsx(
        'relative flex flex-col border-2 border-charcoal-800 bg-white p-6 transition-transform duration-200 hover:-translate-x-[2px] hover:-translate-y-[2px] xl:p-7 2xl:p-8',
        p.featured
          ? clsx('shadow-neobrutal-14', !inCarousel && 'md:-translate-y-3')
          : 'shadow-neobrutal-6',
      )}
    >
      {p.featured && (
        <span className="absolute -top-3.5 left-5 inline-flex items-center gap-1.5 border-2 border-charcoal-800 bg-lime-500 px-3 py-1 font-marketing-mono text-[11px] font-bold uppercase tracking-[0.12em] text-charcoal-800 shadow-neobrutal-2">
          Populaire
        </span>
      )}

      <div className="font-marketing-mono text-[11px] font-medium uppercase tracking-[0.14em] text-charcoal-500">
        {p.num} · {p.name}
      </div>
      <div className="mt-2 font-marketing-display text-[22px] font-bold leading-tight tracking-[-0.02em] text-charcoal-800">
        {p.tagline}
      </div>

      {p.customPrice ? (
        <div className="mt-6 min-h-[74px]">
          <span className="block font-marketing-display text-[34px] font-bold leading-none tracking-[-0.02em] text-charcoal-800 md:text-[38px]">
            {amount}
          </span>
          <span className="mt-3 inline-flex border-2 border-charcoal-800 bg-paper px-3 py-1 font-marketing-mono text-[10px] font-bold uppercase tracking-[0.12em] text-charcoal-700">
            {unit}
          </span>
        </div>
      ) : (
        <div className="mt-5 flex min-h-[74px] items-end gap-2">
          <span className="font-marketing-display text-[48px] font-bold leading-none tracking-[-0.03em] text-charcoal-800 md:text-[56px]">
            {amount}
          </span>
          <span className="pb-2 font-marketing-mono text-[11px] font-medium uppercase tracking-[0.12em] text-charcoal-500">
            {unit}
          </span>
        </div>
      )}
      <div className="mt-2 min-h-5 font-marketing-body text-[12.5px] text-charcoal-500">
        {p.savings}
      </div>

      <div className="mt-5">
        <NeobrutalButton
          as="link"
          to={p.ctaTo}
          variant={p.ctaVariant}
          size="md"
          className="w-full"
        >
          {p.ctaLabel}
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path d="M5 12h14" />
            <path d="M13 6l6 6-6 6" />
          </svg>
        </NeobrutalButton>
      </div>

      <div className="mt-3 text-center font-marketing-mono text-[10px] font-bold uppercase tracking-[0.14em] text-charcoal-500">
        {p.tag}
      </div>

      <div className="mt-6 border-t-2 border-dashed border-charcoal-200" />

      <ul className="mt-6 space-y-3">
        {p.featsBold.map((f) => (
          <li
            key={f}
            className="flex items-start gap-2.5 font-marketing-body text-[14.5px] font-semibold text-charcoal-800"
          >
            <CheckIcon bold />
            {f}
          </li>
        ))}
        {p.feats.map((f) => (
          <li
            key={f}
            className="flex items-start gap-2.5 font-marketing-body text-[14px] text-charcoal-700"
          >
            <CheckIcon />
            {f}
          </li>
        ))}
        {p.featsMuted?.map((f) => (
          <li
            key={f}
            className="flex items-start gap-2.5 font-marketing-body text-[14px] text-charcoal-400 line-through decoration-charcoal-300"
          >
            <MutedIcon />
            {f}
          </li>
        ))}
      </ul>
    </article>
  );
}

export default function PricingPlansGridV2() {
  const { mode } = usePricingMode();

  return (
    <section className="bg-paper pb-20 md:pb-28">
      <div
        className="flex gap-5 overflow-x-auto pt-5 pb-4"
        style={{ paddingLeft: '1.25rem', paddingRight: '1.25rem', scrollbarWidth: 'none' }}
      >
        {PLANS.map((p) => (
          <div key={p.num} className="flex-none" style={{ width: 'min(320px, 82vw)' }}>
            <PlanCard p={p} mode={mode} inCarousel />
          </div>
        ))}
      </div>
    </section>
  );
}
